import { useEffect, useRef, useState } from "react";

import { useSearch } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  deriveEstado,
  useCotizaciones,
  useExtendHold,
  useQuoteActions,
} from "@/hooks/use-cotizaciones";
import { notify } from "@/store/store";
import { CotizacionDetailDialog } from "./CotizacionDetailDialog";

const EXTEND_OPTIONS: { label: string; hours: number }[] = [
  { label: "+1 h", hours: 1 },
  { label: "+2 h", hours: 2 },
  { label: "+6 h", hours: 6 },
  { label: "+12 h", hours: 12 },
  { label: "+1 día", hours: 24 },
  { label: "+30 h (def)", hours: 30 },
];

type StatusFilter = "all" | "pending" | "dismissed";

function EstadoBadge({ estado }: { estado: ReturnType<typeof deriveEstado> }) {
  if (estado === "En Hold") {
    return (
      <span className="inline-flex items-center rounded-full border border-[#b13030] px-2 py-0.5 text-xs font-medium text-[#b13030]">
        En Hold
      </span>
    );
  }
  if (estado === "Reservada") {
    return (
      <Badge variant="default" className="text-xs">
        Reservada
      </Badge>
    );
  }
  if (estado === "Descartada") {
    return (
      <Badge variant="secondary" className="text-xs text-muted-foreground">
        Descartada
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      Pendiente
    </Badge>
  );
}

export default function CotizacionesPage() {
  const { data, isPending } = useCotizaciones();
  const extendHold = useExtendHold();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const search = useSearch({ from: "/cotizaciones" });
  const actions = useQuoteActions(() => setSelectedId(null));

  // Track the last `open` param we acted on so we don't fight user-initiated closes.
  const lastOpenRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!search.open || !data) return;
    if (search.open === lastOpenRef.current) return;
    const match = data.find((q) => q.id === search.open);
    if (match) {
      lastOpenRef.current = search.open;
      setSelectedId(search.open);
    }
  }, [search.open, data]);

  if (isPending) return <Spinner />;

  const selected = data?.find((q) => q.id === selectedId) ?? null;

  const filtered = (data ?? []).filter((q) => {
    if (statusFilter === "all") return true;
    return q.status === statusFilter;
  });

  const count = filtered.length;

  return (
    <div className="flex-1 overflow-y-auto px-6 py-10 sm:py-14 md:pl-24">
      <header className="mb-10 flex flex-wrap items-baseline gap-3">
        <h1 className="text-3xl font-bold tracking-tight">Cotizaciones</h1>
        <span className="text-sm text-text-muted tabular-nums">
          {count} {count === 1 ? "solicitud" : "solicitudes"}
        </span>
        <select
          data-testid="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="h-11 rounded-lg border border-input bg-background px-3 text-sm"
          aria-label="Filtrar por estado"
        >
          <option value="pending">Pendientes</option>
          <option value="dismissed">Descartadas</option>
          <option value="all">Todas</option>
        </select>
      </header>
      {filtered.length === 0 && <p className="text-center text-text-muted">No hay cotizaciones.</p>}
      <div className="max-w-6xl">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Personas</TableHead>
              <TableHead>Fecha tentativa</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((q) => {
              const estado = deriveEstado(q);
              const isTerminal = estado === "Reservada" || estado === "Descartada";
              const hasActiveHold = q.hold?.status === "active";

              return (
                <TableRow
                  key={q.id}
                  onClick={() => setSelectedId(q.id)}
                  className="cursor-pointer"
                  data-selected={selectedId === q.id || undefined}
                >
                  <TableCell>{q.guest_name}</TableCell>
                  <TableCell>{q.pax}</TableCell>
                  <TableCell>
                    {q.tentative_date ?? "—"} {q.tentative_time ?? ""}
                  </TableCell>
                  <TableCell>
                    <EstadoBadge estado={estado} />
                    {hasActiveHold && (q.hold?.table_names?.length ?? 0) > 0 && (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {q.hold!.table_names.join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    {!isTerminal && (
                      <>
                        {hasActiveHold && (
                          <select
                            data-testid="extend-duration-select"
                            className="rounded border px-1 py-0.5 text-sm"
                            defaultValue=""
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              const hours = Number(e.target.value);
                              if (!hours) return;
                              extendHold.mutate(
                                { id: q.id, body: { hours } },
                                { onSuccess: () => notify("success", "Hold extendido", "") },
                              );
                              e.target.value = "";
                            }}
                          >
                            <option value="" disabled>
                              Extender
                            </option>
                            {EXTEND_OPTIONS.map((opt) => (
                              <option key={opt.hours} value={opt.hours}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            actions.reservar(q);
                          }}
                        >
                          Reservar
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            actions.descartar(q);
                          }}
                        >
                          Descartar
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <CotizacionDetailDialog quote={selected} onClose={() => setSelectedId(null)} />
    </div>
  );
}
